import type { ContractorAgreement } from '../types/contractorOnboard';
import {
  contractorAgreementPdfFileName,
  contractorAgreementSignedPdfStoragePath,
} from './contractorAgreementPdfPath';
import { contractorAgreementPdfMeta } from './contractorAgreementPdfLayout';

export {
  contractorAgreementPdfFileName,
  contractorAgreementSignedPdfStoragePath,
} from './contractorAgreementPdfPath';

const FOREST: [number, number, number] = [20, 36, 24];
const OAK: [number, number, number] = [138, 106, 61];
const INK: [number, number, number] = [28, 36, 32];
const MUTED: [number, number, number] = [90, 107, 94];
const CREAM: [number, number, number] = [247, 241, 232];
const LINE: [number, number, number] = [217, 208, 195];
const CARD: [number, number, number] = [255, 253, 248];
const AUDIT: [number, number, number] = [243, 236, 224];
const NOTE: [number, number, number] = [232, 242, 234];

type JsPdfDoc = import('jspdf').jsPDF;

function imageFormat(dataUrl: string): 'PNG' | 'JPEG' | null {
  if (!dataUrl.startsWith('data:image')) return null;
  if (/image\/jpe?g/i.test(dataUrl)) return 'JPEG';
  return 'PNG';
}

function drawSignatureImage(
  pdf: JsPdfDoc,
  dataUrl: string,
  x: number,
  y: number,
  maxW: number,
  maxH: number,
): boolean {
  const format = imageFormat(dataUrl);
  if (!format) return false;
  try {
    const props = pdf.getImageProperties(dataUrl);
    const ratio = props.width / Math.max(props.height, 1);
    let w = maxW;
    let h = w / ratio;
    if (h > maxH) {
      h = maxH;
      w = h * ratio;
    }
    pdf.addImage(dataUrl, format, x, y, w, h);
    return true;
  } catch {
    try {
      pdf.addImage(dataUrl, format, x, y, maxW, maxH);
      return true;
    } catch {
      return false;
    }
  }
}

async function persistSignedAgreementPdf(agreement: ContractorAgreement, blob: Blob): Promise<void> {
  try {
    const { auth, storage } = await import('./firebase');
    if (!auth.currentUser) return;
    const { ref, uploadBytes } = await import('firebase/storage');
    await uploadBytes(ref(storage, contractorAgreementSignedPdfStoragePath(agreement)), blob, {
      contentType: 'application/pdf',
    });
  } catch {
    /* public visitors and missing storage permission */
  }
}

export async function buildContractorAgreementPdfBlob(agreement: ContractorAgreement): Promise<Blob> {
  const { default: jsPDF } = await import('jspdf');
  const pdf = new jsPDF('p', 'mm', 'letter');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 18;
  const contentWidth = pageWidth - margin * 2;
  const meta = contractorAgreementPdfMeta(agreement);
  let y = 20;

  const paintChrome = () => {
    pdf.setFillColor(...CREAM);
    pdf.rect(0, 0, pageWidth, pageHeight, 'F');
    pdf.setFillColor(...FOREST);
    pdf.rect(0, 0, pageWidth, 7, 'F');
    pdf.setFillColor(...OAK);
    pdf.rect(0, 7, pageWidth, 1.2, 'F');
    pdf.setFont('times', 'italic');
    pdf.setFontSize(8);
    pdf.setTextColor(...MUTED);
    pdf.text(
      `${meta.company}  ·  Independent Contractor Agreement  ·  ${meta.contractorName}`,
      pageWidth / 2,
      pageHeight - 9,
      { align: 'center' },
    );
  };

  const ensure = (needed: number) => {
    if (y + needed <= pageHeight - 18) return;
    pdf.addPage();
    paintChrome();
    y = 20;
  };

  const wrapped = (text: string, width = contentWidth): string[] =>
    pdf.splitTextToSize(String(text || ''), width) as string[];

  paintChrome();

  pdf.setFont('times', 'bold');
  pdf.setFontSize(20);
  pdf.setTextColor(...FOREST);
  pdf.text(meta.company, margin, y);
  y += 7;
  pdf.setFont('times', 'italic');
  pdf.setFontSize(9);
  pdf.setTextColor(...MUTED);
  pdf.text(meta.legal, margin, y);
  y += 9;
  pdf.setFont('times', 'normal');
  pdf.setFontSize(16);
  pdf.setTextColor(...INK);
  pdf.text(meta.title, margin, y);
  y += 5;
  pdf.setDrawColor(...OAK);
  pdf.setLineWidth(0.4);
  pdf.line(margin, y, pageWidth - margin, y);
  y += 8;

  meta.facts.forEach(fact => {
    const valueLines = wrapped(fact.value, contentWidth - 42);
    ensure(6 + valueLines.length * 5);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(7);
    pdf.setTextColor(...OAK);
    pdf.text(fact.label.toUpperCase(), margin, y);
    pdf.setFont('times', 'normal');
    pdf.setFontSize(11);
    pdf.setTextColor(...INK);
    valueLines.forEach((line, i) => {
      pdf.text(line || '—', margin + 38, y);
      if (i < valueLines.length - 1) {
        y += 5;
        ensure(6);
      }
    });
    y += 7;
  });
  y += 2;

  meta.sections.forEach(section => {
    const paragraphs = String(section.body || '').split(/\n+/);
    ensure(16);
    pdf.setFont('times', 'normal');
    pdf.setFontSize(14);
    pdf.setTextColor(...FOREST);
    pdf.text(section.heading, margin, y);
    y += 6;
    pdf.setFontSize(10.5);
    pdf.setTextColor(...INK);
    paragraphs.forEach(para => {
      wrapped(para).forEach(line => {
        ensure(6);
        pdf.text(line, margin, y);
        y += 5;
      });
      y += 1.5;
    });
    y += 3;
  });

  const colW = (contentWidth - 8) / 2;
  const sigH = 54;
  ensure(sigH + 10);
  pdf.setFont('times', 'normal');
  pdf.setFontSize(14);
  pdf.setTextColor(...FOREST);
  pdf.text('Signatures', margin, y);
  y += 6;

  const drawSign = (x: number, block: typeof meta.companySign) => {
    pdf.setFillColor(...CARD);
    pdf.setDrawColor(...LINE);
    pdf.setLineWidth(0.3);
    pdf.roundedRect(x, y, colW, sigH, 1.5, 1.5, 'FD');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(7);
    pdf.setTextColor(...OAK);
    pdf.text(block.heading.toUpperCase(), x + 5, y + 6);
    const drewInk = drawSignatureImage(pdf, block.dataUrl, x + 5, y + 9, colW - 12, 22);
    if (!drewInk) {
      pdf.setDrawColor(...LINE);
      pdf.line(x + 5, y + 26, x + colW - 5, y + 26);
      if (block.missingInk) {
        pdf.setFont('times', 'italic');
        pdf.setFontSize(8);
        pdf.setTextColor(...MUTED);
        pdf.text(block.stamped === 'Staff attested (grandfathered)' ? 'Staff attested' : 'Ink not stored', x + 5, y + 24);
      }
    }
    pdf.setFont('times', 'normal');
    pdf.setFontSize(10);
    pdf.setTextColor(...INK);
    pdf.text(block.name, x + 5, y + 36);
    pdf.setFontSize(8);
    pdf.setTextColor(...MUTED);
    pdf.text(block.title, x + 5, y + 41);
    pdf.text(block.stamped, x + 5, y + 46);
  };

  drawSign(margin, meta.companySign);
  drawSign(margin + colW + 8, meta.contractorSign);
  y += sigH + 8;

  const auditRows = meta.audit.map(row => ({
    label: row.label,
    lines: wrapped(row.value, contentWidth - 50),
  }));
  const auditH = 12 + auditRows.reduce((sum, row) => sum + Math.max(row.lines.length, 1) * 5, 0) + 4;
  ensure(auditH + 22);
  pdf.setFillColor(...AUDIT);
  pdf.roundedRect(margin, y, contentWidth, auditH, 1.5, 1.5, 'F');
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(7);
  pdf.setTextColor(...OAK);
  pdf.text('SIGNATURE AUDIT TRAIL', margin + 5, y + 6);
  y += 12;
  auditRows.forEach(row => {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(8);
    pdf.setTextColor(...MUTED);
    pdf.text(`${row.label}:`, margin + 5, y);
    pdf.setFont('times', 'normal');
    pdf.setTextColor(...INK);
    row.lines.forEach((line, i) => {
      pdf.text(line, margin + 46, y);
      if (i < row.lines.length - 1) {
        y += 5;
      }
    });
    y += 5;
  });
  y += 8;

  const noteLines = wrapped(meta.bindingNote, contentWidth - 10);
  const noteH = 8 + noteLines.length * 4.2;
  ensure(noteH);
  pdf.setFillColor(...NOTE);
  pdf.setDrawColor(187, 247, 208);
  pdf.setLineWidth(0.3);
  pdf.roundedRect(margin, y, contentWidth, noteH, 1.5, 1.5, 'FD');
  pdf.setFont('times', 'italic');
  pdf.setFontSize(8);
  pdf.setTextColor(22, 101, 52);
  noteLines.forEach((line, i) => {
    pdf.text(line, margin + 5, y + 6 + i * 4.2);
  });

  const pageCount = pdf.getNumberOfPages();
  for (let i = 1; i <= pageCount; i += 1) {
    pdf.setPage(i);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(...MUTED);
    pdf.text(`Page ${i} of ${pageCount}`, pageWidth - margin, pageHeight - 9, { align: 'right' });
  }

  return pdf.output('blob');
}

export async function contractorAgreementPdfObjectUrl(
  agreement: ContractorAgreement,
  options?: { persist?: boolean },
): Promise<string> {
  const blob = await buildContractorAgreementPdfBlob(agreement);
  if (options?.persist !== false) {
    void persistSignedAgreementPdf(agreement, blob);
  }
  return URL.createObjectURL(blob);
}

export function triggerPdfDownload(url: string, fileName: string): void {
  const link = document.createElement('a');
  link.href = url;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export async function downloadContractorAgreementPdf(
  agreement: ContractorAgreement,
  options?: { persist?: boolean },
): Promise<void> {
  const url = await contractorAgreementPdfObjectUrl(agreement, options);
  triggerPdfDownload(url, contractorAgreementPdfFileName(agreement.fields));
}
